import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { PageBody } from '../../components/Page';
import { Badge } from '../../components/Pill';
import { useShopFile } from '../../components/ShopFileLayout';
import { StatusBanner } from '../../components/StatusBanner';
import { intlLocale } from '../../i18n/formats';
import { useLocale } from '../../i18n/LocaleProvider';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import {
  OpsDocumentKind,
  downloadDocumentPdf,
  listDocuments,
  opsErrorCode,
  regenerateDocument,
  resendDocument,
  saveBlob,
  type OpsDocumentActionResult,
  type OpsDocumentReissueResult,
  type OpsDocumentRow,
} from '../../lib/ops-documents-api';
import {
  documentActions,
  documentKindKey,
  errorKeyForCode,
  pdfFileName,
  resendBody,
  resendProblem,
} from '../../lib/ops-documents';
import { REASON_MIN_LENGTH, isReasonAcceptable } from '../../lib/shop-file';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { ReissueForm } from './ReissueForm';

/** Which write an operator is preparing, on which row. */
type PendingAction =
  | { readonly type: 'resend'; readonly row: OpsDocumentRow }
  | { readonly type: 'regenerate'; readonly row: OpsDocumentRow }
  | { readonly type: 'reissue'; readonly row: OpsDocumentRow };

/** The kind filter's options, in display order. `''` = all kinds. */
const KIND_FILTERS: ReadonlyArray<OpsDocumentKind | ''> = [
  '',
  OpsDocumentKind.Invoice,
  OpsDocumentKind.CreditNote,
  OpsDocumentKind.Cancellation,
];

/**
 * The refusal as the operator should read it: our own copy for a code we know, the server's
 * message otherwise.
 */
function useActionError(): (error: unknown, fallbackKey: string) => string {
  const { t } = useLocale();
  return (error, fallbackKey) => {
    const key = errorKeyForCode(opsErrorCode(error));
    return key ? t(key) : opsErrorMessage(error, t(fallbackKey));
  };
}

/**
 * Document actions on the merchant's behalf (work order item 3): find a document, download the
 * archived PDF, resend it, regenerate one that is MISSING — or reissue an invoice (Storno plus a
 * new invoice from the order's current data).
 *
 * Nothing here changes an issued document (G2). There is no "re-render" of a stored PDF — the
 * action is not offered on a row that has one (D4, § 147 AO) — and a resend sends the archived
 * bytes; a reissue supersedes the document by Storno, the one lawful way to change what it
 * stated. Every write takes a reason, goes through a confirm step and answers with the audit row
 * it wrote; the download is audited by the API before the bytes leave.
 */
export function DocumentsTab() {
  const { t, tCount, locale } = useLocale();
  const { shopId, ticketId, requireAccess } = useShopFile();
  const queryClient = useQueryClient();
  const actionError = useActionError();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search);
  const [kind, setKind] = useState<OpsDocumentKind | ''>('');
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [result, setResult] = useState<{
    readonly type: 'resend' | 'regenerate';
    readonly data: OpsDocumentActionResult;
  } | null>(null);
  const [reissued, setReissued] = useState<OpsDocumentReissueResult | null>(
    null,
  );
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // A new search or filter starts at the first page again.
  useEffect(() => setPage(1), [debouncedSearch, kind]);

  const documents = useQuery({
    queryKey: ['ops-documents', shopId, debouncedSearch.trim(), kind, page],
    queryFn: () =>
      listDocuments(shopId, {
        q: debouncedSearch,
        ...(kind ? { kind } : {}),
        page,
      }),
    retry: false,
  });

  useEffect(() => {
    if (documents.isError && isShopAccessRequired(documents.error, shopId)) {
      requireAccess();
    }
  }, [documents.isError, documents.error, shopId, requireAccess]);

  const download = useMutation({
    mutationFn: async (row: OpsDocumentRow) => {
      const blob = await downloadDocumentPdf(shopId, row.kind, row.id);
      saveBlob(blob, pdfFileName(row.kind, row.number));
    },
    onMutate: () => setDownloadError(null),
    onError: (error) => {
      if (isShopAccessRequired(error, shopId)) return requireAccess();
      setDownloadError(
        actionError(error, 'opsDocuments.errors.downloadFailed'),
      );
    },
  });

  function onDone(
    type: 'resend' | 'regenerate',
    data: OpsDocumentActionResult,
  ) {
    setPending(null);
    setResult({ type, data });
    void queryClient.invalidateQueries({ queryKey: ['ops-documents', shopId] });
  }

  function onReissued(data: OpsDocumentReissueResult) {
    setPending(null);
    setReissued(data);
    void queryClient.invalidateQueries({ queryKey: ['ops-documents', shopId] });
  }

  const totalPages = documents.data
    ? Math.max(1, Math.ceil(documents.data.total / documents.data.pageSize))
    : 1;
  const searching = debouncedSearch.trim() !== '';

  return (
    <PageBody>
      <section className="card space-y-3">
        <h2 className="text-base font-semibold text-gray-900">
          {t('opsDocuments.title')}
        </h2>
        <p className="text-sm text-gray-600">{t('opsDocuments.lead')}</p>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="grow min-w-[min(16rem,100%)]">
            <label htmlFor="opsDocumentsSearch" className="field-label">
              {t('opsDocuments.searchLabel')}
            </label>
            <input
              id="opsDocumentsSearch"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('opsDocuments.searchPlaceholder')}
              maxLength={100}
              className="field-input"
            />
          </div>
          <div>
            <label htmlFor="opsDocumentsKind" className="field-label">
              {t('opsDocuments.kindLabel')}
            </label>
            <select
              id="opsDocumentsKind"
              value={kind}
              onChange={(event) => {
                const next = KIND_FILTERS.find(
                  (option) => option === event.target.value,
                );
                setKind(next ?? '');
              }}
              className="field-input"
            >
              {KIND_FILTERS.map((option) => (
                <option key={option || 'all'} value={option}>
                  {option
                    ? t(documentKindKey(option))
                    : t('opsDocuments.kindAll')}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {result && (
        <StatusBanner tone="success">
          {result.type === 'resend'
            ? t('opsDocuments.result.resendQueued', {
                number: result.data.documentNumber,
                recipient: result.data.recipient ?? '—',
                auditId: result.data.auditLogId,
              })
            : t('opsDocuments.result.regenerateQueued', {
                number: result.data.documentNumber,
                auditId: result.data.auditLogId,
              })}
        </StatusBanner>
      )}
      {reissued && (
        <StatusBanner tone={reissued.isNew ? 'success' : 'warning'}>
          <div className="space-y-1">
            <p>
              {t(
                reissued.isNew
                  ? 'opsDocuments.result.reissued'
                  : 'opsDocuments.result.reissuedExisting',
                {
                  number: reissued.originalDocumentNumber,
                  storno: reissued.stornoDocumentNumber,
                  invoice: reissued.newDocumentNumber,
                  auditId: reissued.auditLogId,
                },
              )}
            </p>
            {reissued.newInvoiceIsReverseCharge && (
              <p>{t('opsDocuments.result.reissuedReverseCharge')}</p>
            )}
            {reissued.calculationErrors.length > 0 && (
              <p>
                {t('opsDocuments.result.reissuedCalculationErrors', {
                  details: reissued.calculationErrors.join('; '),
                })}
              </p>
            )}
          </div>
        </StatusBanner>
      )}
      {downloadError && (
        <StatusBanner tone="error">{downloadError}</StatusBanner>
      )}

      {pending && pending.type === 'reissue' && (
        <ReissueForm
          key={`reissue-${pending.row.id}`}
          row={pending.row}
          shopId={shopId}
          onCancel={() => setPending(null)}
          onDone={onReissued}
          onAccessRequired={requireAccess}
        />
      )}
      {pending && pending.type !== 'reissue' && (
        <ActionForm
          key={`${pending.type}-${pending.row.id}`}
          action={pending}
          shopId={shopId}
          ticketId={ticketId}
          onCancel={() => setPending(null)}
          onDone={onDone}
          onAccessRequired={requireAccess}
        />
      )}

      <section className="card space-y-3">
        {documents.isLoading ? (
          <StatusBanner tone="loading">{t('common.loading')}</StatusBanner>
        ) : documents.isError ? (
          <StatusBanner tone="error">
            {opsErrorMessage(documents.error, t('opsDocuments.loadFailed'))}
          </StatusBanner>
        ) : documents.data && documents.data.data.length > 0 ? (
          <>
            <p className="text-sm text-gray-600">
              {tCount('opsDocuments.count', documents.data.total)}
            </p>
            {documents.data.truncated && (
              <StatusBanner tone="warning">
                {t('opsDocuments.truncated')}
              </StatusBanner>
            )}
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('opsDocuments.columns.number')}</th>
                    <th>{t('opsDocuments.columns.kind')}</th>
                    <th>{t('opsDocuments.columns.order')}</th>
                    <th>{t('opsDocuments.columns.issued')}</th>
                    <th>{t('opsDocuments.columns.total')}</th>
                    <th>{t('opsDocuments.columns.status')}</th>
                    <th>{t('opsDocuments.columns.recipient')}</th>
                    <th>{t('opsDocuments.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.data.data.map((row) => {
                    const actions = documentActions(row);
                    return (
                      <tr key={`${row.kind}-${row.id}`}>
                        <td className="font-mono text-xs whitespace-nowrap">
                          {row.number}
                          {!row.isActive && (
                            <>
                              {' '}
                              <Badge tone="gray">
                                {t('opsDocuments.superseded')}
                              </Badge>
                            </>
                          )}
                        </td>
                        <td>{t(documentKindKey(row.kind))}</td>
                        <td className="font-mono text-xs">
                          {row.orderNumber ?? '—'}
                        </td>
                        <td className="whitespace-nowrap">
                          {new Date(row.issueDate).toLocaleDateString(
                            intlLocale(locale),
                          )}
                        </td>
                        <td className="whitespace-nowrap">
                          {Number(row.totalGross).toLocaleString(
                            intlLocale(locale),
                            { style: 'currency', currency: row.currency },
                          )}
                        </td>
                        <td>
                          <Badge
                            tone={
                              row.status.includes('FAILED')
                                ? 'red'
                                : row.status === 'EMAIL_SENT'
                                  ? 'green'
                                  : 'gray'
                            }
                          >
                            {row.status}
                          </Badge>
                          {row.heldForManualSend && (
                            <div className="text-xs text-amber-700 mt-1">
                              {t('opsDocuments.heldBadge')}
                            </div>
                          )}
                          {row.emailLastError && (
                            <div
                              className="text-xs text-red-700 mt-1 max-w-xs truncate"
                              title={row.emailLastError}
                            >
                              {row.emailLastError}
                            </div>
                          )}
                        </td>
                        <td className="text-xs break-all">
                          {row.customerEmail ?? '—'}
                        </td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {actions.canDownload && (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                disabled={download.isPending}
                                onClick={() => download.mutate(row)}
                              >
                                {t('opsDocuments.actions.download')}
                              </button>
                            )}
                            {row.hasPdf && (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                disabled={!actions.canResend}
                                title={
                                  actions.resendBlockedKey
                                    ? t(actions.resendBlockedKey)
                                    : undefined
                                }
                                onClick={() => {
                                  setResult(null);
                                  setPending({ type: 'resend', row });
                                }}
                              >
                                {t('opsDocuments.actions.resend')}
                              </button>
                            )}
                            {actions.canRegenerate && (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                onClick={() => {
                                  setResult(null);
                                  setPending({ type: 'regenerate', row });
                                }}
                              >
                                {t('opsDocuments.actions.regenerate')}
                              </button>
                            )}
                            {row.kind === OpsDocumentKind.Invoice && (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                disabled={!actions.canReissue}
                                title={
                                  actions.reissueBlockedKey
                                    ? t(actions.reissueBlockedKey)
                                    : undefined
                                }
                                onClick={() => {
                                  setResult(null);
                                  setReissued(null);
                                  setPending({ type: 'reissue', row });
                                }}
                              >
                                {t('opsDocuments.actions.reissue')}
                              </button>
                            )}
                          </div>
                          {actions.resendBlockedKey && row.hasPdf && (
                            <p className="text-xs text-gray-500 mt-1 max-w-xs">
                              {t(actions.resendBlockedKey)}
                            </p>
                          )}
                          {actions.reissueBlockedKey && (
                            <p className="text-xs text-gray-500 mt-1 max-w-xs">
                              {t(actions.reissueBlockedKey)}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!searching && totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="btn-neutral btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => current - 1)}
                >
                  {t('opsDocuments.previous')}
                </button>
                <span className="text-sm text-gray-600">
                  {t('opsDocuments.page', { page, pages: totalPages })}
                </span>
                <button
                  type="button"
                  className="btn-neutral btn-sm"
                  disabled={page >= totalPages || page >= 40}
                  onClick={() => setPage((current) => current + 1)}
                >
                  {t('opsDocuments.next')}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-600">
            {searching ? t('opsDocuments.noMatch') : t('opsDocuments.none')}
          </p>
        )}
      </section>
    </PageBody>
  );
}

/**
 * The form for one write, then its confirm step.
 *
 * Two steps on purpose: the form collects the reason (and, for a resend, an optional other
 * address with the ticket that authorises it); the dialog then states in one place what is about
 * to happen — who receives the document, that the merchant's own alert fires if it fails — before
 * anything is sent.
 */
function ActionForm({
  action,
  shopId,
  ticketId,
  onCancel,
  onDone,
  onAccessRequired,
}: {
  readonly action: Exclude<PendingAction, { type: 'reissue' }>;
  readonly shopId: string;
  readonly ticketId: string | null;
  readonly onCancel: () => void;
  readonly onDone: (
    type: 'resend' | 'regenerate',
    data: OpsDocumentActionResult,
  ) => void;
  readonly onAccessRequired: () => void;
}) {
  const { t } = useLocale();
  const actionError = useActionError();
  const { row } = action;
  const actions = documentActions(row);
  const isResend = action.type === 'resend';

  const [reason, setReason] = useState('');
  const [override, setOverride] = useState('');
  // Prefilled with the ticket the agent came from — that ticket is usually the instruction.
  const [ticket, setTicket] = useState(ticketId ?? '');
  const [confirming, setConfirming] = useState(false);

  const form = { reason, recipientOverride: override, ticketId: ticket };
  const problem = isResend
    ? resendProblem(form, actions.resendNeedsOverride)
    : isReasonAcceptable(reason)
      ? null
      : 'opsDocuments.form.reasonTooShort';
  const recipient = override.trim() || row.customerEmail || '—';
  const overriding = override.trim() !== '';

  const submit = useMutation({
    mutationFn: () =>
      isResend
        ? resendDocument(shopId, row.kind, row.id, resendBody(form))
        : regenerateDocument(shopId, row.kind, row.id, reason.trim()),
    onSuccess: (data) => onDone(action.type, data),
    onError: (error) => {
      setConfirming(false);
      if (isShopAccessRequired(error, shopId)) onAccessRequired();
    },
  });

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (problem) return;
    setConfirming(true);
  }

  return (
    <section className="card space-y-3">
      <h2 className="text-base font-semibold text-gray-900">
        {t(
          isResend
            ? 'opsDocuments.form.resendTitle'
            : 'opsDocuments.form.regenerateTitle',
          { number: row.number },
        )}
      </h2>
      <p className="text-sm text-gray-600">
        {t(
          isResend
            ? 'opsDocuments.form.resendLead'
            : 'opsDocuments.form.regenerateLead',
        )}
      </p>

      <form onSubmit={onSubmit} className="space-y-3">
        {isResend && (
          <>
            <div>
              <label htmlFor="opsResendOverride" className="field-label">
                {t('opsDocuments.form.overrideLabel')}
              </label>
              <input
                id="opsResendOverride"
                type="email"
                value={override}
                onChange={(event) => setOverride(event.target.value)}
                placeholder={row.customerEmail ?? ''}
                maxLength={254}
                className="field-input"
              />
              <p className="field-hint">
                {t(
                  actions.resendNeedsOverride
                    ? 'opsDocuments.form.overrideHintRequired'
                    : 'opsDocuments.form.overrideHint',
                )}
              </p>
            </div>
            {overriding && (
              <div>
                <label htmlFor="opsResendTicket" className="field-label">
                  {t('opsDocuments.form.ticketLabel')}
                </label>
                <input
                  id="opsResendTicket"
                  value={ticket}
                  onChange={(event) => setTicket(event.target.value)}
                  maxLength={64}
                  className="field-input font-mono"
                />
                <p className="field-hint">
                  {t('opsDocuments.form.ticketHint')}
                </p>
              </div>
            )}
          </>
        )}

        <div>
          <label htmlFor="opsDocumentReason" className="field-label">
            {t('opsDocuments.form.reasonLabel')}
          </label>
          <textarea
            id="opsDocumentReason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={500}
            className="field-input"
          />
          <p className="field-hint">
            {t('opsDocuments.form.reasonHint', { min: REASON_MIN_LENGTH })}
          </p>
        </div>

        {problem && reason.trim() !== '' && (
          <p className="text-sm text-amber-700">{t(problem)}</p>
        )}
        {submit.isError && (
          <StatusBanner tone="error">
            {actionError(
              submit.error,
              isResend
                ? 'opsDocuments.errors.resendFailed'
                : 'opsDocuments.errors.regenerateFailed',
            )}
          </StatusBanner>
        )}

        <div className="flex gap-2">
          <button
            type="submit"
            className="btn-primary btn-sm"
            disabled={problem !== null || submit.isPending}
          >
            {t('opsDocuments.form.continue')}
          </button>
          <button
            type="button"
            className="btn-neutral btn-sm"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        title={t(
          isResend
            ? 'opsDocuments.confirm.resendTitle'
            : 'opsDocuments.confirm.regenerateTitle',
        )}
        confirmLabel={t(
          isResend
            ? 'opsDocuments.confirm.resendLabel'
            : 'opsDocuments.confirm.regenerateLabel',
        )}
        confirmDisabled={submit.isPending}
        onConfirm={() => submit.mutate()}
        onCancel={() => setConfirming(false)}
      >
        {isResend ? (
          <div className="space-y-2">
            <p>
              {t('opsDocuments.confirm.resendBody', {
                number: row.number,
                recipient,
              })}
            </p>
            {overriding && <p>{t('opsDocuments.confirm.overrideMarksSent')}</p>}
            <p>{t('opsDocuments.confirm.resendFailureAlert')}</p>
          </div>
        ) : (
          <p>
            {t('opsDocuments.confirm.regenerateBody', { number: row.number })}
          </p>
        )}
      </ConfirmDialog>
    </section>
  );
}
